<template><section><!-- ---
title: 计算机网络系列之网络模型OSI与TCP/IP
date: 2022-10-13
tags: 计算机网络
set: Network
--- -->
<p>互联网的信息传输依赖于各种协议，不同的环节遵循不同的协议，这些协议分布在不同层级共同实现了网络功能，这些层级结构的模型组成常见的有三种说法，分别是TCP/IP的五层模型和OSI的七层模型。</p>
<h2>一. OSI模型</h2>
<p>OSI(Open Systems Interconnection, 开放系统互联参考模型)，是ISO在20世纪80年代提出的，将计算机网络通信协议分为七层。</p>
<div style="display:flex;justify-content:center;"><img src="https://zhangmingemma.github.io/dist/images/2022-10-13/2.jpg" style="display:inline-block; margin-bottom:16px; width:500px;"></div>
<p>我们以公司A和公司B的一次商业报价单发送为例子进行每个层的功能的解释：</p>
<h3>1. 应用层</h3>
<p>给用户提供网络服务，包含FTP（文件）、SMTP（邮件）、HTTP、DNS、TFTP、SNMP协议。许多大家熟悉的基于Internet的应用实现都离不开这些协议，最常见的，进行万维网(WWW)的访问用到了HTTP协议，文件传输用FTP，电子邮件发送用SMTP，域名解析用DNS，远程登录用TELNET等。</p>
<ul>
<li><strong>HTTP(Hypertext Transfer Protocol, 超文本传输协议)</strong>: 是请求-响应的协议，主要服务的场景是web服务，就是网页的传输，默认使用TCP的80端口，更详细的关于HTTP的解释可以看看另外一篇文章<a href="https://zhangmingemma.github.io/#/post?file=2022-10-14-HTTP">《计算机网络系列之HTTP基础》</a></li>
<li><strong>HTTPS</strong>：是以安全为目标的HTTP通道，在HTTP基础上通过传输加密和身份认证保证了传输过程的安全性，默认使用TCP的443端口</li>
<li><strong>DNS(Domain Name System, 域名系统)</strong>: 将域名和IP地址进行相互映射，默认使用UDP的53端口，更详细的介绍可以看<a href="https://zhangmingemma.github.io/#/post?file=2022-10-16-DNS">《计算机网络系列之DNS以及网络域名解析》</a></li>
<li><strong>FTP(File Transfer Protocol, 文件传输协议)</strong>: FTP允许用户以文件操作的方式与另一主机进行相互通信，用户并不是真正登录到想要存取的计算机上面成为完全用户，而是通过FTP程序访问远程资源，实现用户往返传输文件、目录管理以及访问电子邮件等，默认使用TCP的20/21端口</li>
<li><strong>TFTP(Trivival File Transer Protocol, 简单文件传输协议)</strong>: 提供不复杂、开销不大的文件传输服务，默认使用UDP的69端口</li>
<li><strong>SNMP(Simple Network Management Protocol, 简单网络管理协议)</strong>: 原来的名字叫简单网管监控协议，专门设计用于在IP网格管理网络节点的一种标准协议，使网络管理员能够管理网格效能，发现并解决网络问题以及规划网络增长，默认使用UDP的161端口</li>
<li><strong>TELNET</strong>: 是Internet远程登录服务的标准协议和主要方式，为用户提供了在本地计算机完成远程主机工作的能力，默认使用TCP的23端口</li>
<li><strong>SSH(Secure Shell)</strong>: 较可靠的，专为远程登录会话和网络服务提供安全性的协议，默认使用TCP的22端口</li>
</ul>
<p>在我们的例子中，公司A老板就是我们的用户，发送商业报价单就是一种网络服务，类比发送商业合同也是一个网络服务。</p>
<h3>2. 表示层</h3>
<p>表示层提供各种应用于应用层的数据的编码和转换功能，确保一个系统的应用层发送的数据能被另一个系统的应用层识别，将接收到的数据转换为计算机二进制数据。数据的压缩和加密也是表示层可提供的功能之一。在我们的例子中，公司A在中国，公司B在英国，要商定英文作为交流的语言，那次是表示层也就是公司的文秘员，就需要将商业报价单翻译为英文，同时为了防止泄密，还会对报价单进行加密处理，这就是表示层的作用。</p>
<h3>3. 会话层</h3>
<p>会话层负责建立、管理和终止表示层实体之间的通信对话，该层的通信由不同设备中的应用程序之间的服务请求和响应组成，在我们的例子中，会话层的同事拿到表示层的同事转换后的资料，会话层同事将记录本次会话，找到公司B的地址和联系方式，将整份资料装进信封，写上地址和联系方式，交由传输层将快递寄出，等到公司B接收成功，会话就算结束，会话层的同事就会终止此次会话。</p>
<h3>4. 传输层</h3>
<p>为应用层实体提供主机端到端的通信能力，包括处理差错控制和流量控制等问题，保证了数据包的顺序传送及数据的完整性。由于一台主机可以运行多个进程，因此运输层有复用和分用两种能力，复用就是多个应用层进程可以同时使用传输层的服务，分用是指运输层把收到的信息分别交付给应用层的不同进程。运输层包含TCP、UDP两大协议，更细节的分析及两者的区别，可以看看另一篇文章<a href="https://zhangmingemma.github.io/#/post?file=2022-10-15-TCP%26UDP">《计算机网络系列之TCP与UDP》</a></p>
<ul>
<li><strong>TCP(Transmission Control Protocol, 传输控制协议)</strong>: 面向连接的、可靠的、基于字节流的传输层通信协议</li>
<li><strong>UDP(User Datagram Protocol, 用户数据协议)</strong>: 为应用程序提供了一种无需建立连接就可以发送封装的IP数据包的方法，是一种无连接的、不可靠的、快速传输的通信协议。</li>
</ul>
<p>在我们的例子中，传输层就是相当于公司服务快递邮件收发的人，负责将上层要寄出的资料投送到快递公司或者邮局</p>
<h3>5. 网络层</h3>
<p>主要解决主机到主机的通信问题，网络层包含的协议涉及数据包在整个网络中的逻辑传输，通过IP寻址来建立两个节点之间的连接，为源端的运输层送来的分组，选择合适的路由和交换节点，正确无误地按照地址传送给目的端的运输层。在发送数据时，网络层把传输层产生的报文段或用户数据封装成分组或包进行传送，也就是IP数据报。IP协议是Internet的基础，我们常见的路由器就属于网络层。该层主要包含IP、ICMP、IGMP三个主要协议。</p>
<ul>
<li><strong>ICMP(Internet Control Message Protocol, 控制报文协议)</strong>: 用于在IP主机、路由器之间传递控制消息，控制消息包含网络是否连通、主机是否可达、路由是否可用等网络本身的消息，例如ping</li>
<li><strong>IGMP(Internet Group Management Protocol, 组管理协议)</strong>: 主机和组播路由器间运行的协议。其中单播是1对1发送消息，组播是1对N发送消息，广播是1对全部发送消息</li>
<li><strong>ARP(Address Resolution Protocol, 地址解析协议)</strong>: 是根据IP协议获取物理地址的一个协议。主机发送消息时将包含目标IP地址的ARP请求广播到局域网络中的所有主机，并接受返回消息，以此确定目标的物理地址。收到返回消息后将该IP地址和物理地址存入本机ARP缓存中并保留一定时间，下次请求时直接查询ARP缓存以节约资源。</li>
<li><strong>RARP(Reverse Address Resolution Protocol, 逆地址解析协议)</strong>: 允许局域网的物理机器从网关服务器的ARP表或者缓存上请求其IP协议。</li>
</ul>
<p>在我们的例子中，网络层就是相当于快递公司的庞大网络、全国不同的集散中心，比如深圳发往北京的快递，首先要到深圳集散中心，再到武汉集散中心，再去北京集散中心，每个集散中心就相当于网络中的一个IP节点。</p>
<h3>6. 数据链路层</h3>
<p>负责监视数据在主机和网络之间的交换，在两个邻近节点进行数据传送的时候，将网络层交来的IP数据报组装成帧，每一帧包含数据和必要的控制信息（地址信息、同步信息、差错控制等），其中的控制信息使接收端能够知道一个帧从哪个比特开始，到哪个比特结束。控制信息还使接收端能够检测到所收到的帧中有无差错，如果发现差错，数据链路层就会丢弃这个出现差错的帧，如果需要改正数据，则需要采用可靠传输协议来纠正出现的错误。数据链路层又分为2个子层：逻辑链路控制子层(LLC)和媒体访问控制子层(MAC)。常见的网卡就是属于数据链路层的。</p>
<h3>7. 物理层</h3>
<p>网络实际最终信号的传输是通过物理层实现的，通过物理介质传输比特流。规定了电平、速度和电缆针脚。常用的设备有集线器、中继器、网线、同轴电缆等。</p>
<h2>二. TCP/IP参考模型</h2>
<p>TCP/IP（Transmission Control Protocol / Internet Protocol，传输控制协议/网际协议）不是指这两个具体的协议，其中包含了多种协议类型，如IP、ICMP、TCP、UDP、TELNET、FTP以及HTTP，是网际协议族，只是因为TCP和IP两个协议比较具有代表性，所以叫TCP/IP协议。TCP/IP参考模型的诞生起源于担心珍贵的主机、路由器和互联网关可能会突然崩溃，但不想网络受子网硬件损失的影响，已经建立的会话不会被取消，想要整个网络体系较为灵活。TCP/IP参考模型自上而下包含应用层、传输层、网络层、网络接口四层。其中数据链接层和物理层合并成为网络接口层，应用层对应OSI模型中的应用层、表示层和会话层。但后来为了更方便地描述，并综合OSI和TCP/IP的优点，采用的都是五层网络模型，包含应用层、传输层、网络层、数据链路层、物理层。</p>
<div style="display:flex;justify-content:center;"><img src="https://zhangmingemma.github.io/dist/images/2022-10-13/1.jpeg" style="display:inline-block; margin-bottom:16px"></div>
<p>在实际应用中，OSI模型常被作为理论的参考模型被广泛讨论和使用，而TCP/IP协议紧密结合实际已经作为网络互连的事实标准。</p>
<h3>数据传输过程</h3>
<img src="https://zhangmingemma.github.io/dist/images/2022-10-13/3.jpg">
<p>假定主机1的应用进程AP1，想要跟主机2的应用进程AP2进行通信，整个通信过程可以概括为：</p>
<ul>
<li>应用层：AP1将数据交给本主机的第5层应用层，应用层加上必要的控制信息，并将数据转换为二进制数据，转换后交给传输层</li>
<li>传输层：第4层传输层收到数据单元后，加上本层的控制信息，TCP头部报文(TCP头部的关键信息 - 端口号)，再交给网络层</li>
<li>网络层：第3层网络层收到上一层的数据包后，加上本层控制信息，即IP头部(IP头部的关键信息 - IP地址)，在交给数据链路层</li>
<li>数据链路层：第2层数据链路层收到数据包后，加上本层的控制信息，即MAC头部(MAC头部的关键信息 - MAC地址)，再交给物理层</li>
<li>物理层：将二进制数据组成比特流转换为电信号，通过传输介质到达目的主机2</li>
</ul>
<img src="https://zhangmingemma.github.io/dist/images/2022-10-13/4.jpg">
<p>到达目的主机2后，就从目的主机2的物理层逐次上升到应用层，</p>
<ul>
<li>物理层：将电信号转换为二进制数据</li>
<li>数据链路层：查看目标MAC地址，吻合则拆掉MAC头部，否则丢弃数据</li>
<li>网络层：查看目标IP地址，吻合就拆掉IP头部，否则丢弃数据</li>
<li>传输层：根据TCP头部判断数据段运送到哪里，之后将被分组的数据段重组，运送到应用层</li>
<li>应用层：将二进制数据还原成原始信息交给目的进程AP2</li>
</ul>
<h2>四. 小结</h2>
<p>总结一下OSI七层模型，每层的主要作用</p>
<ol>
<li>应用层：网络服务与最终用户的一个接口</li>
<li>表示层：数据的表示、安全和压缩</li>
<li>会话层：建立、管理和终止会话</li>
<li>传输层：定义传输数据的协议端口号，以及控流</li>
<li>网络层：定义逻辑地址、实现的不同</li>
<li>数据链路层：建立逻辑连接、进行硬件地址寻址、差错校验等</li>
<li>物理层：建立、维护、断开物理连接</li>
</ol>
<p>参考文献：
[1] <a href="https://juejin.cn/post/7049265346296938504">《OSI七层模型与TCP/IP五层模型》</a>
[2] 《计算机网络》谢希仁著</p>
</section></template>

